import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { AISubmissionInsightCard } from '@/components/analytics/AISubmissionInsightCard';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { 
  Search, 
  Filter, 
  Download, 
  MoreVertical, 
  FileText,
  Loader2,
  Sparkles,
  Zap,
  Info,
  Trash2,
  Calendar,
  TrendingUp,
  ExternalLink,
  Eye,
  Phone,
  Cpu,
  Terminal,
  Wallet,
  CheckCircle,
  BarChart3,
  Edit,
  Save,
  MessageSquare,
  Users,
  BrainCircuit
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { exportToCSV, exportToExcel } from '@/utils/exportUtils';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Submission {
  id: string;
  customerName: string;
  customerPhone: string;
  formName: string;
  status: 'pending' | 'contacted' | 'completed' | 'cancelled';
  amount?: string;
  submittedAt: string;
  data: Record<string, any>;
  aiAnalysis?: {
    sentiment: 'positive' | 'neutral' | 'negative';
    intent: string;
    summary: string;
    priority: 'low' | 'medium' | 'high';
    key_items: string[];
  };
  processingAttempts?: number;
  lastProcessedAt?: string;
  completionTime?: number;
  userAgent?: string;
  ipAddress?: string;
  deviceType?: string;
}

const StorageFileLink = ({ path, className }: { path: string, className?: string }) => {
  const [loading, setLoading] = useState(false);

  const handleOpen = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setLoading(true);
    try {
      // If it's already a full URL (legacy), just open it
      if (path.startsWith('http')) {
        window.open(path, '_blank');
        return;
      }

      const { data, error } = await supabase.storage
        .from('form-uploads')
        .createSignedUrl(path, 3600);
        
      if (error) throw error;
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (err) {
      console.error('Error generating signed URL:', err);
    } finally {
      setLoading(false);
    }
  };

  const fileName = path.split('/').pop() || 'View File';

  return (
    <button 
      onClick={handleOpen}
      disabled={loading}
      className={cn("text-blue-500 hover:underline inline-flex items-center gap-1 text-left", className)}
    >
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
      <span className="truncate max-w-[150px]">{fileName}</span>
    </button>
  );
};

const Submissions = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { hasAIInsights } = usePlanLimits();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [formFilter, setFormFilter] = useState<string>('all');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [forms, setForms] = useState<{id: string, title: string}[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState<Submission | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const activeForm = formId ? forms.find(f => f.id === formId) : null;
  const activeFormTitle = activeForm?.title;

  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!user) return;
      
      setIsLoading(true);
      
      try {
        // Fetch submissions with form data
        let query = supabase
          .from('submissions')
          .select(`
            id,
            data,
            submitted_at,
            status,
            ai_analysis,
            processing_attempts,
            last_processed_at,
            completion_time,
            user_agent,
            ip_address,
            device_type,
            forms!inner(title, user_id, fields)
          `)
          .eq('forms.user_id', user.id);

        if (formId) {
          query = query.eq('form_id', formId);
        }

        const { data: submissionsData, error: submissionsError } = await query.order('submitted_at', { ascending: false });

        if (submissionsError) {
          console.error('Error fetching submissions:', submissionsError);
          return;
        }

        // Fetch user's forms for the filter dropdown (or header title if isolated)
        const { data: formsData, error: formsError } = await supabase
          .from('forms')
          .select('id, title')
          .eq('user_id', user.id);

        if (formsError) {
          console.error('Error fetching forms:', formsError);
        }

        // Transform submissions data
        const transformedSubmissions = submissionsData?.map(sub => {
          const submissionData = sub.data as Record<string, any>;
          
          // Try to find name, email, and phone - prioritized detection
          let customerName = 'Anonymous Respondent';
          let customerEmail = 'No email provided';
          let customerPhone = 'No phone provided';
          
          const entries = Object.entries(submissionData);
          
          // 1. Email Extraction (Strict First Match)
          for (const [key, value] of entries) {
            if (!key || typeof value !== 'string') continue;
            const lowerKey = key.toLowerCase();
            if (lowerKey.includes('email') && value.includes('@')) {
              customerEmail = value.trim();
              break; // Stop after first match
            }
          }
          
          // 2. Name Extraction (Strict First Match)
          // Priority: Explicit keywords -> First text-like value
          const nameKeywords = ['full name', 'fullname', 'name', 'client', 'customer'];
          let foundName = false;
          
          for (const [key, value] of entries) {
            if (!key || typeof value !== 'string' || !value.trim()) continue;
            const lowerKey = key.toLowerCase();
            if (nameKeywords.some(k => lowerKey === k || lowerKey === `your ${k}` || lowerKey.includes('name'))) {
              if (!lowerKey.includes('business') && !lowerKey.includes('company') && !lowerKey.includes('email')) {
                customerName = value.trim();
                foundName = true;
                break; // Stop after first match
              }
            }
          }
          
          if (!foundName) {
            const firstText = entries.find(([k, v]) => 
              typeof v === 'string' && 
              v.trim().length > 2 && 
              !v.includes('@') && 
              !v.match(/^\+?\d{10,}$/) &&
              !k.toLowerCase().includes('id')
            );
            if (firstText) customerName = String(firstText[1]).trim();
          }
          
          // 3. Phone Extraction (Strict First Match)
          const phoneKeywords = ['phone', 'whatsapp', 'mobile', 'contact', 'tel'];
          for (const [key, value] of entries) {
            if (!key || typeof value !== 'string' || !value.trim()) continue;
            const lowerKey = key.toLowerCase();
            if (phoneKeywords.some(k => lowerKey.includes(k))) {
              customerPhone = value.trim();
              break; // Stop after first match
            }
          }
          
          if (customerPhone === 'No phone provided') {
            const phoneValue = entries.find(([_, v]) => 
              typeof v === 'string' && v.trim().match(/^\+?\d{10,}$/)
            );
            if (phoneValue) customerPhone = String(phoneValue[1]).trim();
          }

          const formFields = Array.isArray(sub.forms?.fields) ? sub.forms.fields : [];
          
          const displayData: Record<string, any> = {};
          
          formFields.forEach((f: any) => {
            const value = submissionData[f.id] || submissionData[f.label];
            if (value !== undefined && value !== null) displayData[f.label] = value;
          });
          
          Object.entries(submissionData).forEach(([k, v]) => {
            if (!formFields.find((f: any) => f.id === k || f.label === k)) {
               displayData[k] = v;
            }
          });

          return {
            id: sub.id,
            customerName,
            customerPhone,
            formName: sub.forms?.title || 'Unknown Form',
            status: (sub.status || 'pending') as 'pending' | 'contacted' | 'completed' | 'cancelled',
            submittedAt: sub.submitted_at,
            data: displayData,
            aiAnalysis: sub.ai_analysis as Submission['aiAnalysis']
          };
        }) || [];

        setSubmissions(transformedSubmissions);
        setForms(formsData || []);
      } catch (error) {
        console.error('Error fetching submissions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubmissions();
  }, [user]);

  // Polling for AI analysis of the latest submission
  useEffect(() => {
    if (!submissions.length || !user || !hasAIInsights) return;

    const latest = submissions[0];
    if (!latest.aiAnalysis) {
      const interval = setInterval(async () => {
        console.log('Polling for AI analysis for submission:', latest.id);
        const { data, error } = await supabase
          .from('submissions')
          .select('ai_analysis')
          .eq('id', latest.id)
          .single();

        if (!error && data?.ai_analysis) {
          setSubmissions(prev => 
            prev.map(s => s.id === latest.id ? { ...s, aiAnalysis: data.ai_analysis as Submission['aiAnalysis'] } : s)
          );
          clearInterval(interval);
        }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [submissions, user, hasAIInsights]);

  const filteredSubmissions = submissions.filter(submission => {
    const matchesSearch = 
      submission.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      submission.customerPhone.includes(searchQuery) ||
      submission.formName.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;
    const matchesForm = formFilter === 'all' || submission.formName === formFilter;
    
    return matchesSearch && matchesStatus && matchesForm;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'contacted':
        return 'secondary';
      case 'pending':
        return 'outline';
      case 'cancelled':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'contacted': return 'Contacted';
      case 'pending': return 'Pending';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExport = (format: 'csv' | 'excel') => {
    const exportData = filteredSubmissions.map(sub => ({
      'Respondent Name': sub.customerName,
      'Phone': sub.customerPhone,
      'Form': sub.formName,
      'Status': sub.status,
      'Submitted At': formatDate(sub.submittedAt),
      ...sub.data,
    }));

    const filename = `submissions_${new Date().toISOString().split('T')[0]}`;
    
    if (format === 'csv') {
      exportToCSV(exportData, filename);
    } else {
      exportToExcel(exportData, filename);
    }
    
    toast({
      title: 'Export Successful',
      description: `Submissions exported as ${(format || "").toUpperCase()}`,
    });
  };

  const handleMarkAsContacted = async (submissionId: string) => {
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ status: 'contacted' })
        .eq('id', submissionId);

      if (error) throw error;

      // Update local state
      setSubmissions(prev => 
        prev.map(sub => 
          sub.id === submissionId 
            ? { ...sub, status: 'contacted' as const }
            : sub
        )
      );

      toast({
        title: "Status updated",
        description: "Submission marked as contacted.",
      });
    } catch (error) {
      console.error('Error updating submission status:', error);
      toast({
        title: "Error",
        description: "Failed to update submission status.",
        variant: "destructive"
      });
    }
  };

  const handleContactCustomer = (submission: Submission) => {
    // Clean phone number - remove all non-digits first, then handle country codes
    const cleanPhone = submission.customerPhone.replace(/\D/g, '');
    
    // If it starts with country code, keep it, otherwise it might be local number
    if (cleanPhone.length < 8) {
      toast({
        title: "Invalid phone number",
        description: "The phone number appears to be invalid.",
        variant: "destructive"
      });
      return;
    }
    
    const message = `Hi ${submission.customerName}, thank you for your submission via Bravax Forms. We'll get back to you shortly!`;
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    // Automatically mark as contacted
    handleMarkAsContacted(submission.id);
  };

  const handleViewDetails = (submission: Submission) => {
    navigate(`/submissions/${submission.id}`);
  };

  const handleDeleteSubmission = async () => {
    if (!submissionToDelete) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('submissions')
        .delete()
        .eq('id', submissionToDelete.id);

      if (error) throw error;

      toast({
        title: "Submission deleted",
        description: "The response has been removed successfully.",
      });

      // Update local state
      setSubmissions(prev => prev.filter(sub => sub.id !== submissionToDelete.id));
      setDeleteDialogOpen(false);
      setSubmissionToDelete(null);
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast({
        title: "Error",
        description: "Failed to delete submission.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDelete = (submission: Submission) => {
    setSubmissionToDelete(submission);
    setDeleteDialogOpen(true);
  };

  const thisMonthSubmissions = submissions.filter(sub => {
    const submittedDate = new Date(sub.submittedAt);
    const currentDate = new Date();
    return submittedDate.getMonth() === currentDate.getMonth() && 
           submittedDate.getFullYear() === currentDate.getFullYear();
  });

  const stats = [
    {
      title: "Total Submissions",
      value: submissions.length.toString(),
      description: "All time",
      icon: FileText,
      color: "text-blue-600"
    },
    {
      title: "This Month",
      value: thisMonthSubmissions.length.toString(),
      description: "Current month",
      icon: Calendar,
      color: "text-green-600"
    },
    {
      title: "Active Forms",
      value: forms.length.toString(),
      description: "Total forms",
      icon: TrendingUp,
      color: "text-primary"
    },
    {
      title: "Avg per Form",
      value: forms.length > 0 ? Math.round(submissions.length / forms.length).toString() : '0',
      description: "Submissions per form",
      icon: MessageSquare,
      color: "text-orange-600"
    }
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading submissions...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>Submissions | Bravax Forms</title>
      </Helmet>
      <div className="p-4 sm:p-6 lg:p-8 space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              {activeFormTitle ? `${activeFormTitle} Submissions` : 'All Submissions'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {activeFormTitle 
                ? "Review and manage responses specifically mapped to this form."
                : "Manage and review all your form responses in one place."}
            </p>
          </div>
          {submissions.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button 
                variant="outline" 
                className="w-full sm:w-auto border-amber-500/20 text-amber-600 hover:bg-amber-500/5 group"
                onClick={() => window.location.href = '/analytics'}
              >
                <Sparkles className="mr-2 h-4 w-4 text-amber-500 group-hover:animate-pulse" />
                View Full Analytics
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="w-full sm:w-auto">
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleExport('csv')}>
                    Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('excel')}>
                    Export as Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* AI First Value Experience (FVE) Cards */}
        {submissions.length > 0 && (
          <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-700">
            <div className="flex items-center justify-between">
               <div className="flex items-center gap-2">
                 <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                 <span className="text-xs font-bold text-amber-600 dark:text-amber-500 uppercase tracking-widest">
                   {hasAIInsights ? "Smart Response Analysis" : "Premium AI Insights (Upgrade Required)"}
                 </span>
               </div>
               {!hasAIInsights && (
                 <Button variant="link" className="text-amber-600 text-xs h-auto p-0" onClick={() => window.location.href = '/billing'}>
                   Unlock detailed insights →
                 </Button>
               )}
            </div>

            {hasAIInsights ? (
              <div className={cn(
                "grid gap-4",
                submissions.length <= 3 ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" : "grid-cols-1"
              )}>
                {submissions.slice(0, submissions.length <= 3 ? 3 : 1).map((sub, idx) => (
                  <AISubmissionInsightCard
                    key={sub.id}
                    customerName={sub.customerName}
                    formName={sub.formName}
                    analysis={sub.aiAnalysis}
                    isProcessing={!sub.aiAnalysis && sub.status === 'pending'}
                  />
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-amber-500/30 bg-amber-500/5">
                <CardContent className="py-8 text-center">
                  <BrainCircuit className="h-10 w-10 text-amber-500/50 mx-auto mb-3" />
                  <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">
                    AI is processing your responses in the background. Upgrade to see sentiment, intent, and priorities!
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {stats.map((stat, index) => (
            <Card key={index} className="card-elegant hover:shadow-glow transition-all duration-300 animate-scale-in" style={{ animationDelay: `${index * 100}ms` }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-xl sm:text-2xl font-bold text-foreground">{stat.value}</div>
                <p className="text-xs text-muted-foreground">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters - Only show if there are submissions */}
        {submissions.length > 0 && (
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">Filter Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, phone, or form..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {!formId && forms.length > 0 && (
                  <div className="flex-1 w-full min-w-[200px]">
                    <Select value={formFilter} onValueChange={setFormFilter}>
                      <SelectTrigger>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Filter className="h-4 w-4" />
                          <SelectValue placeholder="Filter by form" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Forms</SelectItem>
                        {forms.map((form) => (
                          <SelectItem key={form.id} value={form.title}>{form.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submissions Table */}
        <Card className="card-elegant">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">
              {submissions.length === 0 ? 'Form Submissions' : 'Recent Submissions'}
            </CardTitle>
            {submissions.length > 0 && (
              <CardDescription>
                {filteredSubmissions.length} of {submissions.length} submissions
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {submissions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No submissions yet</h3>
                <p className="text-muted-foreground mb-4">
                  Your forms haven't received any submissions yet. Share your forms to start collecting responses!
                </p>
                <Button variant="outline" onClick={() => window.location.href = '/forms'}>
                  View My Forms
                </Button>
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No submissions found</h3>
                <p className="text-muted-foreground">
                  No submissions match your current filters. Try adjusting your search criteria.
                </p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Respondent</TableHead>
                      <TableHead>Form</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Fields</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubmissions.map((submission) => (
                      <TableRow key={submission.id} className="hover:bg-muted/50">
                        <TableCell>
                          <div>
                            <div className="font-medium text-foreground">{submission.customerName}</div>
                            <div className="text-sm text-muted-foreground flex items-center">
                              <Phone className="h-3 w-3 mr-1" />
                              {submission.customerPhone}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-48 truncate">{submission.formName}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge variant={getStatusColor(submission.status)}>
                              {getStatusText(submission.status)}
                            </Badge>
                            {submission.aiAnalysis && (
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] h-4 py-0",
                                  submission.aiAnalysis.sentiment === 'positive' && "bg-success/10 text-success border-success/20",
                                  submission.aiAnalysis.sentiment === 'negative' && "bg-destructive/10 text-destructive border-destructive/20",
                                  submission.aiAnalysis.sentiment === 'neutral' && "bg-muted text-muted-foreground"
                                )}
                              >
                                <Zap className="w-2 h-2 mr-0.5" />
                                {submission.aiAnalysis.intent}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {submission.aiAnalysis?.summary ? (
                            <div className="max-w-[250px] relative group">
                              <p className="text-sm italic text-muted-foreground line-clamp-2">
                                "{submission.aiAnalysis.summary}"
                              </p>
                              <div className="flex items-center gap-1 mt-1">
                                <Sparkles className="w-3 h-3 text-amber-500" />
                                <span className="text-[10px] font-medium text-amber-600 dark:text-amber-500 uppercase tracking-wider">AI Insight</span>
                              </div>
                            </div>
                          ) : Object.keys(submission.data).length > 0 ? (
                            <div className="max-w-[200px]">
                              {Object.entries(submission.data).slice(0, 2).map(([key, value], idx) => {
                                const isFile = typeof value === 'string' && (value.startsWith('http') || value.startsWith('forms/'));
                                return (
                                <div key={idx} className="text-sm">
                                  <span className="font-medium">{key}:</span>{' '}
                                  {isFile ? (
                                    <StorageFileLink path={String(value)} />
                                  ) : (
                                    <span className="truncate max-w-[200px] inline-block align-bottom">{String(value)}</span>
                                  )}
                                </div>
                                );
                              })}
                              {Object.keys(submission.data).length > 2 && (
                                <span className="text-xs text-muted-foreground">+{Object.keys(submission.data).length - 2} more</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">No data</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(submission.submittedAt)}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => handleViewDetails(submission)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleContactCustomer(submission)}>
                                <MessageSquare className="mr-2 h-4 w-4" />
                                Contact Respondent
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleMarkAsContacted(submission.id)}>
                                <Users className="mr-2 h-4 w-4" />
                                Mark as Contacted
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => confirmDelete(submission)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Response
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>



        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="max-w-[400px]">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Delete Response?
              </AlertDialogTitle>
              <AlertDialogDescription className="pt-2">
                Are you sure you want to delete this submission from <span className="font-bold text-foreground">{submissionToDelete?.customerName}</span>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-6">
              <AlertDialogCancel disabled={isDeleting} className="border-none hover:bg-muted">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteSubmission();
                }}
                disabled={isDeleting}
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground shadow-lg shadow-destructive/20"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Yes, Delete'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default Submissions;